import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import "./Tags.css";

class Tags extends Component {
  state = {
    tags: [],
    formData: {
      id: null,
      tag_name: "",
      tag_status: "1",
    },
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    deleting: false,
    modalMode: "add",
    searchTerm: "",
    statusFilter: "all",
    currentPage: 1,
    itemsPerPage: 10,
  };

  componentDidMount() {
    this.fetchTags();
  }

  fetchTags = async () => {
    this.setState({ loading: true, error: "" });

    try {
      const res = await axios.get("/products/tags");
      const data = res.data?.data || [];

      this.setState({
        tags: Array.isArray(data) ? data : [],
        currentPage: 1,
      });
    } catch (err) {
      this.setState({
        error: err.response?.data?.message || "Failed to load tags.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
      error: "",
    }));
  };

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value, currentPage: 1 });
  };

  handleStatusFilterChange = (e) => {
    this.setState({ statusFilter: e.target.value, currentPage: 1 });
  };

  handleItemsPerPageChange = (e) => {
    this.setState({ itemsPerPage: Number(e.target.value), currentPage: 1 });
  };

  clearFilters = () => {
    this.setState({ searchTerm: "", statusFilter: "all", currentPage: 1 });
  };

  openModal = (mode = "add", tag = null) => {
    const modalElement = document.getElementById("tagModal");
    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);

    this.setState(
      {
        modalMode: mode,
        formData:
          mode === "edit" && tag
            ? {
                id: tag.id,
                tag_name: tag.tag_name || "",
                tag_status: tag.tag_status?.toString() || "1",
              }
            : {
                id: null,
                tag_name: "",
                tag_status: "1",
              },
        error: "",
      },
      () => modal.show()
    );
  };

  closeModal = () => {
    const modalElement = document.getElementById("tagModal");
    if (modalElement && window.bootstrap) {
      const modal = window.bootstrap.Modal.getInstance(modalElement);
      if (modal) modal.hide();
    }
    this.resetForm();
  };

  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        tag_name: "",
        tag_status: "1",
      },
      error: "",
      submitting: false,
      modalMode: "add",
    });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;
    const tagName = formData.tag_name.trim();

    if (!tagName) {
      return this.setState({ error: "Tag name is required." });
    }

    this.setState({ submitting: true, error: "" });

    const payload = {
      tag_name: tagName,
      tag_status: formData.tag_status,
    };

    try {
      if (modalMode === "add") {
        await axios.post("/products/tags", payload);
      } else {
        await axios.put(`/products/tags/${formData.id}`, payload);
      }

      await this.fetchTags();
      this.closeModal();

      Swal.fire({
        icon: "success",
        title: modalMode === "add" ? "Tag added successfully!" : "Tag updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      this.setState({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving tag.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modalElement = document.getElementById("deleteModal");
    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);
    modal.show();
  };

  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    this.setState({ deleting: true });

    try {
      await axios.delete(`/products/tags/${deleteId}`);
      await this.fetchTags();

      const modalElement = document.getElementById("deleteModal");
      if (modalElement && window.bootstrap) {
        const modal = window.bootstrap.Modal.getInstance(modalElement);
        if (modal) modal.hide();
      }

      this.setState({ deleteId: null });

      Swal.fire({
        icon: "success",
        title: "Tag deleted successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to delete tag.",
        text: err.response?.data?.message || "Please try again.",
      });
    } finally {
      this.setState({ deleting: false });
    }
  };

  getFilteredTags = () => {
    const { tags, searchTerm, statusFilter } = this.state;
    const keyword = searchTerm.trim().toLowerCase();

    return tags.filter((tag) => {
      const name = (tag.tag_name || "").toLowerCase();
      const status = tag.tag_status?.toString() || "0";

      const matchesSearch = !keyword || name.includes(keyword);
      const matchesStatus = statusFilter === "all" || status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  };

  getPageNumbers = (totalPages) => {
    const { currentPage } = this.state;
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

    const pages = [1];
    if (currentPage > 4) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (currentPage < totalPages - 3) pages.push("...");
    pages.push(totalPages);

    return pages;
  };

  goToPage = (page) => {
    const { currentPage, itemsPerPage } = this.state;
    const totalPages = Math.max(1, Math.ceil(this.getFilteredTags().length / itemsPerPage));
    let nextPage = page;

    if (page === "prev") nextPage = currentPage - 1;
    if (page === "next") nextPage = currentPage + 1;
    if (typeof nextPage !== "number") return;

    nextPage = Math.min(totalPages, Math.max(1, nextPage));
    if (nextPage !== currentPage) this.setState({ currentPage: nextPage });
  };

  render() {
    const {
      tags,
      formData,
      error,
      loading,
      submitting,
      deleting,
      modalMode,
      searchTerm,
      statusFilter,
      currentPage,
      itemsPerPage,
    } = this.state;

    const filteredTags = this.getFilteredTags();
    const totalPages = Math.max(1, Math.ceil(filteredTags.length / itemsPerPage));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * itemsPerPage;
    const currentTags = filteredTags.slice(startIndex, startIndex + itemsPerPage);
    const startRecord = filteredTags.length ? startIndex + 1 : 0;
    const endRecord = Math.min(startIndex + itemsPerPage, filteredTags.length);

    const activeCount = tags.filter((t) => t.tag_status?.toString() === "1").length;
    const inactiveCount = tags.length - activeCount;
    const pageNumbers = this.getPageNumbers(totalPages);

    return (
      <div className="pc-container tags-page">
        <div className="pc-content">
          <div className="tags-shell">
            {/* Header */}
            <div className="tags-topbar">
              <div>
                <div className="tags-breadcrumb">
                  <span>Catalog</span>
                  <i className="ph ph-caret-right" />
                  <strong>Tags</strong>
                </div>
                <h2 className="tags-title">Tags</h2>
                <p className="tags-subtitle">
                  Organize product tags, labels, and availability status.
                </p>
              </div>

              <button
                type="button"
                className="bookly-primary-btn"
                onClick={() => this.openModal("add")}
              >
                <i className="ph ph-plus" />
                <span>New Tag</span>
              </button>
            </div>

            {/* Summary Grid */}
            <div className="tag-summary-grid">
              <div className="tag-summary-card">
                <div className="tag-summary-icon blue">
                  <i className="ph ph-tag" />
                </div>
                <div>
                  <span className="summary-label">Total Tags</span>
                  <strong>{tags.length}</strong>
                </div>
              </div>

              <div className="tag-summary-card">
                <div className="tag-summary-icon green">
                  <i className="ph ph-check-circle" />
                </div>
                <div>
                  <span className="summary-label">Active</span>
                  <strong>{activeCount}</strong>
                </div>
              </div>

              <div className="tag-summary-card">
                <div className="tag-summary-icon pink">
                  <i className="ph ph-pause-circle" />
                </div>
                <div>
                  <span className="summary-label">Inactive</span>
                  <strong>{inactiveCount}</strong>
                </div>
              </div>
            </div>

            {/* Main Card */}
            <div className="tag-card">
              <div className="tag-card-header">
                <div>
                  <h4>Tag List</h4>
                  <p>
                    {filteredTags.length} result
                    {filteredTags.length !== 1 ? "s" : ""} found
                  </p>
                </div>
              </div>

              {/* Toolbar */}
              <div className="tag-toolbar">
                <div className="tag-search-wrap">
                  <i className="ph ph-magnifying-glass" />
                  <input
                    type="text"
                    className="tag-search"
                    placeholder="Search tag name..."
                    value={searchTerm}
                    onChange={this.handleSearchChange}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="tag-search-clear"
                      onClick={() => this.setState({ searchTerm: "", currentPage: 1 })}
                    >
                      <i className="ph ph-x" />
                    </button>
                  )}
                </div>

                <div className="tag-filter-group">
                  <label htmlFor="tagStatusFilter">Status</label>
                  <select
                    id="tagStatusFilter"
                    className="tag-filter-select"
                    value={statusFilter}
                    onChange={this.handleStatusFilterChange}
                  >
                    <option value="all">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                <div className="tag-filter-group">
                  <label htmlFor="tagRows">Rows</label>
                  <select
                    id="tagRows"
                    className="tag-filter-select"
                    value={itemsPerPage}
                    onChange={this.handleItemsPerPageChange}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                {(searchTerm || statusFilter !== "all") && (
                  <button
                    type="button"
                    className="tag-reset-btn"
                    onClick={this.clearFilters}
                  >
                    <i className="ph ph-arrow-counter-clockwise" />
                    Reset
                  </button>
                )}
              </div>

              {error && !loading && (
                <div className="tag-alert">
                  <i className="ph ph-warning-circle" />
                  <span>{error}</span>
                  <button type="button" onClick={this.fetchTags}>
                    Retry
                  </button>
                </div>
              )}

              {loading ? (
                <div className="tag-loading">
                  <div className="spinner-border" role="status" aria-hidden="true" />
                  <span>Loading tags...</span>
                </div>
              ) : (
                <>
                  <div className="tag-table-wrap">
                    <table className="tag-table">
                      <thead>
                        <tr>
                          <th className="col-number">#</th>
                          <th>Tag Name</th>
                          <th className="col-status">Status</th>
                          <th className="col-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentTags.length ? (
                          currentTags.map((tag, index) => {
                            const isActive =
                              tag.tag_status === "1" || tag.tag_status === 1;
                            return (
                              <tr key={tag.id}>
                                <td className="col-number table-index">
                                  {startIndex + index + 1}
                                </td>
                                <td>
                                  <div className="tag-name-cell">
                                    <div className="tag-badge-icon">
                                      <i className="ph ph-hash" />
                                    </div>
                                    <div>
                                      <span className="tag-name">{tag.tag_name}</span>
                                      <small>ID #{tag.id}</small>
                                    </div>
                                  </div>
                                </td>
                                <td className="col-status">
                                  <span
                                    className={`tag-status-badge ${
                                      isActive ? "active" : "inactive"
                                    }`}
                                  >
                                    <span className="status-dot" />
                                    {isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td className="col-actions">
                                  <div className="tag-actions">
                                    <button
                                      type="button"
                                      className="tag-action-btn edit"
                                      onClick={() => this.openModal("edit", tag)}
                                      title="Edit tag"
                                    >
                                      <i className="ph ph-note-pencil" />
                                    </button>
                                    <button
                                      type="button"
                                      className="tag-action-btn delete"
                                      onClick={() => this.confirmDelete(tag.id)}
                                      title="Delete tag"
                                    >
                                      <i className="ph ph-trash" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="4">
                              <div className="tag-empty-state">
                                <div className="empty-icon">
                                  <i className="ph ph-tag" />
                                </div>
                                <h5>No tags found</h5>
                                <p>
                                  {searchTerm || statusFilter !== "all"
                                    ? "Try changing your search query or filters."
                                    : "Add your first tag to get started."}
                                </p>
                                {searchTerm || statusFilter !== "all" ? (
                                  <button
                                    type="button"
                                    className="tag-reset-btn"
                                    onClick={this.clearFilters}
                                  >
                                    Clear Filters
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="bookly-secondary-btn"
                                    onClick={() => this.openModal("add")}
                                  >
                                    <i className="ph ph-plus" />
                                    Add Tag
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredTags.length > 0 && (
                    <div className="tag-pagination-wrap">
                      <div className="tag-pagination-info">
                        Showing <strong>{startRecord}</strong> to{" "}
                        <strong>{endRecord}</strong> of{" "}
                        <strong>{filteredTags.length}</strong> tags
                      </div>

                      {totalPages > 1 && (
                        <nav aria-label="Tag pagination">
                          <ul className="tag-pagination">
                            <li className={safeCurrentPage === 1 ? "disabled" : ""}>
                              <button
                                type="button"
                                onClick={() => this.goToPage("prev")}
                                disabled={safeCurrentPage === 1}
                              >
                                <i className="ph ph-caret-left" />
                              </button>
                            </li>
                            {pageNumbers.map((page, index) =>
                              page === "..." ? (
                                <li key={`ellipsis-${index}`} className="ellipsis">
                                  <span>...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={safeCurrentPage === page ? "active" : ""}
                                >
                                  <button type="button" onClick={() => this.goToPage(page)}>
                                    {page}
                                  </button>
                                </li>
                              )
                            )}
                            <li className={safeCurrentPage === totalPages ? "disabled" : ""}>
                              <button
                                type="button"
                                onClick={() => this.goToPage("next")}
                                disabled={safeCurrentPage === totalPages}
                              >
                                <i className="ph ph-caret-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        <div className="modal fade" id="tagModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content tag-modal">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header tag-modal-header">
                  <div>
                    <span className="tag-modal-kicker">
                      {modalMode === "add" ? "Create" : "Update"}
                    </span>
                    <h5 className="modal-title">
                      {modalMode === "add" ? "Add Tag" : "Edit Tag"}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="tag-modal-close"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                  >
                    <i className="ph ph-x" />
                  </button>
                </div>

                <div className="modal-body tag-modal-body">
                  {error && (
                    <div className="tag-form-alert">
                      <i className="ph ph-warning-circle" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="tag-form-group">
                    <label htmlFor="tagName">
                      Tag Name <span>*</span>
                    </label>
                    <div className="tag-input-wrap">
                      <i className="ph ph-tag" />
                      <input
                        id="tagName"
                        type="text"
                        name="tag_name"
                        className="tag-input"
                        placeholder="e.g. Best Seller, New Arrival..."
                        value={formData.tag_name}
                        onChange={this.handleChange}
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="tag-form-group mb-0">
                    <label>Status</label>
                    <div className="tag-status-options">
                      <label
                        className={`status-option ${
                          formData.tag_status === "1" ? "selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="tag_status"
                          value="1"
                          checked={formData.tag_status === "1"}
                          onChange={this.handleChange}
                        />
                        <span>
                          <i className="ph ph-check-circle" /> Active
                        </span>
                      </label>
                      <label
                        className={`status-option ${
                          formData.tag_status === "0" ? "selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="tag_status"
                          value="0"
                          checked={formData.tag_status === "0"}
                          onChange={this.handleChange}
                        />
                        <span>
                          <i className="ph ph-pause-circle" /> Inactive
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="modal-footer tag-modal-footer">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bookly-primary-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className={modalMode === "add" ? "ph ph-plus" : "ph ph-check"} />
                        {modalMode === "add" ? "Add Tag" : "Update Tag"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content delete-modal">
              <div className="delete-modal-body">
                <div className="delete-warning-icon">
                  <i className="ph ph-trash" />
                </div>
                <h5>Delete Tag?</h5>
                <p>
                  This tag will be permanently removed. This action cannot be undone.
                </p>
                <div className="delete-modal-actions">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bookly-danger-btn"
                    onClick={this.handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="ph ph-trash" />
                        Yes, Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Tags;